import React, { useState } from 'react';
import { Modifier, usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';

import { IComponentBaseProps } from '../models/IComponentBaseProps.js';

export interface IPopperProps extends IComponentBaseProps {
	/**
	 * The required element Popper will position your content around.
	 * @title Anchor Element
	 */
	anchorElement: HTMLElement;
	/**
	 * Where should the popup display relative to the tooltip icon.
	 * @title Position of popup
	 */
	placement?: Placement;
	/**
	 * The approach taken by popper for positioning.
	 * @title Strategy
	 */
	strategy?: 'fixed' | 'absolute';
	/**
	 * Used to define custom popperModifiers.
	 * @title Modifiers
	 */
	modifiers?: Partial<Modifier<unknown, object>>[];
}

export const stripPopperProps = <P extends IPopperProps>(props: Partial<P>) => {
	const { anchorElement, placement, strategy, ...otherProps } = props;
	return otherProps;
}

export const withPopper = <P extends IPopperProps>(WrappedComponent: React.ComponentType<P>) => {
	return (props: P) => {
		const { placement = 'bottom-start', strategy, modifiers, anchorElement } = props;
		if (!anchorElement) {
			return <WrappedComponent {...props}/>
		}

		const [referenceElement] = useState(anchorElement);
		const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
		const { styles, attributes } = usePopper(referenceElement, popperElement, {
			placement,
			strategy,
			modifiers,
		});
		return (
			<div className={'popper'} ref={setPopperElement} style={styles.popper} {...attributes}>
				<WrappedComponent {...props}/>
			</div>
		);
	};
};
